// PANOUL INSTALAȚIILOR, în meniul vizualizatorului: elementul ales (cu tot ce
// scrie un proiectant lângă o țeavă sau un cablu pe planșă: diametru, pantă,
// tronsoane, fitinguri, piesele legate, ruta până la stradă — pe care o poate
// aprinde întreagă; la un cablu circuitul lui, cablul, tubul, de unde vine și
// în ce intră; la tablou schema lui, circuit cu circuit), verificările (un
// clic arată elementele vinovate), valorile folosite cu sursa lor — ce e
// implicit sau sugerat e „de confirmat” (plan §2) — și cantitățile pe sistem
// și diametru, țevi, cabluri, tuburi, doze și fitinguri, cum intră în deviz.

import { useFormat, useLimba, useT, type Cheie, type Valori } from "../i18n";
import { ro } from "../i18n/ro";
import { useInstalatii } from "./context";
import {
  SISTEME, cantitati, cantitatiFitinguri, cantitatiPaturi, cantitatiTuburi, lungime, rutaSpreColector, rutaSpreSursa,
  type Circuit, type Fiting, type ModelInstalatii, type Segment, type Sistem, type TipCircuit,
} from "./model";
import "./instalatii.css";

export default function PanouInstalatii() {
  const t = useT();
  const { numar } = useFormat();
  const inst = useInstalatii();
  if (!inst) return null;
  const numeSistem = (s: Sistem) => t(SISTEME.find((x) => x.sistem === s)!.nume);
  // cifrele din parametri, cu virgula sau punctul limbii, nu cu punctul din model
  const valori = (p?: Record<string, string | number>): Valori | undefined => p && Object.fromEntries(
    Object.entries(p).map(([k, v]) => [k, typeof v === "number" ? numar(v, { maximumFractionDigits: 2 }) : v]));

  const m = inst.model;
  const limba = useLimba();
  const doze = m.echipamente.filter((e) => e.fel === "doza").length;
  const tuburi = cantitatiTuburi(m);
  const paturi = cantitatiPaturi(m);
  const articole = m.cantitati_articole ?? [];
  return (
    <div className="inst-panou">
      {inst.ales && <Ales m={m} id={inst.ales} numeSistem={numeSistem} />}
      <div>
        <b>{t("inst.verificari")}</b>
        {m.verificari.map((v) => (
          <button key={v.regula} type="button" className="inst-verif" disabled={!v.elemente.length}
                  title={v.sursa}
                  onClick={() => inst.alege(v.elemente[0] ?? null)}>
            <span className={`inst-verdict ${v.verdict}`}>{v.verdict}</span>
            <span>
              {t(v.mesaj, valori(v.param))}
              <br /><span className="inst-sursa">{v.regula} · {v.sursa}</span>
            </span>
          </button>
        ))}
      </div>
      {m.intrari.length > 0 && <div>
        <b>{t("inst.intrari")}</b>
        <table className="inst-tabel"><tbody>
          {m.intrari.map((x) => (
            <tr key={x.cheie}>
              <td>{t(x.cheie)}</td>
              <td>{t(x.valoare, valori(x.param))}
                <span className="inst-sursa"> · {t(`inst.sursa.${x.sursa}` as const)}</span></td>
            </tr>
          ))}
        </tbody></table>
      </div>}
      {articole.length > 0 && <div>
        <b>{t("inst.cantitati.articole")}</b>
        <table className="inst-tabel"><tbody>
          {articole.map((q) => (
            <tr key={q.articol}>
              <td>{q.nume[limba === "en" ? "en" : "ro"]}</td>
              <td className="nr">
                {q.lungime_m != null && `${numar(q.lungime_m, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m`}
                {q.lungime_m != null && q.bucati ? <br /> : null}
                {q.bucati ? t("inst.cantitati.buc", { n: q.bucati }) : null}
              </td>
            </tr>
          ))}
        </tbody></table>
      </div>}
      <div>
        <b>{t("inst.cantitati")}</b>
        <table className="inst-tabel"><tbody>
          {cantitati(m).map((q) => (
            <tr key={`${q.sistem}-${q.dn}-${q.cablu ?? ""}`}>
              <td>{numeSistem(q.sistem)}</td>
              <td className="nr">{q.cablu ? numeCablu(q.cablu) : !q.dn ? "—"
                : q.sistem === "electrice" ? `${q.dn} mm²` : `DN ${q.dn}`}</td>
              <td className="nr">{numar(q.ml, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td>
            </tr>
          ))}
          {tuburi.map((q) => (
            <tr key={`tub-${q.tub_mm}`}>
              <td>{numeSistem("electrice")}</td>
              <td className="nr">{t("inst.cantitati.tub", { d: q.tub_mm })}</td>
              <td className="nr">{numar(q.ml, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td>
            </tr>
          ))}
          {paturi.map((q) => (
            <tr key={`pat-${q.latime_mm}`}>
              <td>{numeSistem("electrice")}</td>
              <td className="nr">{t("inst.cantitati.pat", { l: q.latime_mm })}</td>
              <td className="nr">{numar(q.ml, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td>
            </tr>
          ))}
          {doze > 0 && (
            <tr key="doze">
              <td>{numeSistem("electrice")}</td>
              <td className="nr">{t("inst.echipament.doza")}</td>
              <td className="nr">{t("inst.cantitati.buc", { n: doze })}</td>
            </tr>
          )}
          {cantitatiFitinguri(m).map((q) => (
            <tr key={`${q.sistem}-${q.fel}-${q.dn}-${q.unghi}-${q.dn_intrare ?? ""}`}>
              <td>{numeSistem(q.sistem)}</td>
              <td className="nr">{numeFiting(t, numar, q)} · DN {q.dn}</td>
              <td className="nr">{t("inst.cantitati.buc", { n: q.bucati })}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

type Numar = ReturnType<typeof useFormat>["numar"];

/** Numele unui fiting, cum îl cere instalatorul: «cot 45°», «ramificație
 *  87,5° redusă 110/50». */
function numeFiting(t: ReturnType<typeof useT>, numar: Numar, f: Pick<Fiting, "fel" | "unghi" | "dn_intrare"> & { dn?: number }) {
  const unghi = numar(f.unghi, { maximumFractionDigits: 1 });
  if (f.fel === "ramificatie" && f.dn_intrare && f.dn) {
    return t("inst.fiting.redusa", { unghi, dn: f.dn, dn_intrare: f.dn_intrare });
  }
  return t(f.fel === "cot" ? "inst.fiting.cot" : "inst.fiting.ramificatie", { unghi });
}

/** Cablul, cum îl scrie proiectantul: «3 × 2,5 mm²»; curenții slabi n-au secțiune. */
const numeCablu = (cablu: string) => (cablu === "date" ? "UTP / coaxial" : `${cablu.replace("x", " × ")} mm²`);

/** Rolul, cum îl citește omul: la cablul unui apartament (sau al serviciilor
 *  comune), bucata lui — pe palier, pe coloană, pe tavanul parterului —, nu
 *  «conductă principală». */
function numeRol(t: ReturnType<typeof useT>, s: Pick<Segment, "sistem" | "rol" | "circuit">): string {
  if (s.sistem === "electrice" && (s.circuit === "coloana" || s.circuit === "comun")
      && (s.rol === "alimentare" || s.rol === "coloana" || s.rol === "principala")) {
    return t(`inst.rol.cablu.${s.circuit}.${s.rol}` as const);
  }
  return t(`inst.rol.${s.rol}` as const);
}

/** Numele unui apartament: al câtelea e pe nivelul lui, în ordinea
 *  documentului («Ap. 3»), ca omul să-l recunoască în plan. */
function numeApartament(m: ModelInstalatii, id: string): string {
  const a = m.apartamente?.find((x) => x.id === id);
  if (!a) return id;
  const peNivel = (m.apartamente ?? []).filter((x) => x.storey === a.storey);
  return `Ap. ${peNivel.indexOf(a) + 1}`;
}

/** Textul din catalog pentru o cheie care poate lipsi (un fel de piesă nou). */
const din = (t: ReturnType<typeof useT>, cheie: string, altfel: string) =>
  (cheie in ro ? t(cheie as Cheie) : altfel);

/** Ce e elementul ales — ce scrie un proiectant lângă o țeavă pe planșă:
 *  sistemul, rolul, diametrul, materialul, pe unde trece, panta, cotele,
 *  lungimea, tronsoanele cu panta fiecăruia, coturile și ramificațiile,
 *  piesele pe care le leagă, verdictele care îl numesc și ruta lui până la
 *  stradă, pe care o poate aprinde întreagă în plan și în 3D. La un cablu:
 *  circuitul, cablul și tubul lui, de unde vine și în ce aparat intră; la
 *  tablou: schema lui. */
function Ales({ m, id, numeSistem }: { m: ModelInstalatii; id: string; numeSistem: (s: Sistem) => string }) {
  const t = useT();
  const inst = useInstalatii()!;
  const { numar } = useFormat();
  const cm = (x: number) => numar(x * 100, { maximumFractionDigits: 1 });
  const doua = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const s = m.segmente.find((x) => x.id === id);
  const e = s ? null : m.echipamente.find((x) => x.id === id);
  if (!s && !e) return null;
  const verdicte = m.verificari.filter((v) => v.verdict !== "pass" && v.elemente.includes(id));
  const numeCapat = (cid: string): string => {
    const ap = m.aparate?.find((x) => x.id === cid);
    if (ap) return din(t, `aparat.${ap.kind}`, ap.kind);
    const ech = m.echipamente.find((x) => x.id === cid);
    if (ech) return t(`inst.echipament.${ech.fel}` as const);
    const seg = m.segmente.find((x) => x.id === cid);
    return seg ? numeRol(t, seg) : cid;
  };
  const limba = useLimba();
  /** Articolul, cum se cumpără: «Țeavă PP Ø110 (bară 3 m) · 3 bare · 2 îmbinări». */
  const numeArticol = (x: { articol?: string; bucati?: number; imbinari?: number }): string => {
    const a = x.articol ? m.articole?.[x.articol] : undefined;
    const nume = a ? a.nume[limba === "en" ? "en" : "ro"] : x.articol ?? "";
    const parti = [nume];
    if (x.bucati && a?.lungime_bara_m) {
      const plural = a.fel === "cablu" ? "tambure" : a.fel === "teava" && a.lungime_bara_m >= 50 ? "colaci" : "bare";
      const forma = x.bucati === 1 ? ({ tambure: "tambur", colaci: "colac", bare: "bara" } as const)[plural] : plural;
      parti.push(t(`inst.camp.bucati.${forma}` as const, { n: x.bucati, l: numar(a.lungime_bara_m) }));
    }
    if (x.imbinari) parti.push(t("inst.camp.imbinari", { n: x.imbinari }));
    return parti.join(" · ");
  };
  const randuri: [string, string][] = s ? randuriSegment(s) : [
    [t("inst.camp.sistem"), numeSistem(e!.sistem)],
    [t("inst.camp.echipament"), t(`inst.echipament.${e!.fel}` as const)],
    [t("inst.camp.dimensiuni"), e!.dim.map((x) => numar(x, doua)).join(" × ") + " m"],
    ...(e!.articol ? [[t("inst.camp.articol"), numeArticol(e!)] as [string, string]] : []),
  ];
  const ruta = s ? rutaSpreColector(m, s.id) : [];
  const rutaAprinsa = ruta.length > 1 && ruta.every((x) => x === id || inst.evidentiate.has(x));
  // de unde vine: drumul în amonte, până la coloana clădirii și la branșament
  const sursa = s ? rutaSpreSursa(m, s.id) : [];
  const sursaAprinsa = sursa.length > 1 && sursa.every((x) => x === id || inst.evidentiate.has(x));
  const dupa = new Map(m.segmente.map((x) => [x.id, x]));
  // circuitul firului ales, întreg (toate firele lui), de aprins dintr-un clic
  const circuit = s?.sistem === "electrice" && s.circuit && s.flat
    ? m.segmente.filter((x) => x.sistem === "electrice" && x.flat === s.flat && x.circuit === s.circuit).map((x) => x.id)
    : [];
  const circuitAprins = circuit.length > 1 && circuit.every((x) => x === id || inst.evidentiate.has(x));
  // schema tabloului: a tabloului ales, sau a tabloului apartamentului din
  // care face parte firul ales (strânsă, ca panoul să rămână scurt)
  const tablouAles = !!e?.circuite;
  const schema = e?.circuite
    ?? (s?.sistem === "electrice" && s.flat
        ? m.echipamente.find((x) => x.fel === "tablou" && x.flat === s.flat)?.circuite ?? [] : []);

  function randuriSegment(s: Segment): [string, string][] {
    const piese = (s.piese ?? []).map((pid) => {
      const p = m.piese.find((x) => x.id === pid);
      return p ? din(t, `mobilier.${p.kind}`, p.kind) : pid;
    });
    const fitinguri = new Map<string, number>();
    for (const f of s.fitinguri ?? []) {
      const k = numeFiting(t, numar, { ...f, dn: s.dn });
      fitinguri.set(k, (fitinguri.get(k) ?? 0) + 1);
    }
    const z0 = s.puncte[0][2], z1 = s.puncte[s.puncte.length - 1][2];
    const cablu = s.sistem === "electrice";
    const tip = s.tip_circuit ?? (s.rol === "circuit" ? "comun" : "coloana");
    const ap = s.flat ? m.apartamente?.find((a) => a.id === s.flat) : undefined;
    return [
      [t("inst.camp.sistem"), numeSistem(s.sistem)],
      [t("inst.camp.rol"), numeRol(t, s)],
      ...(ap ? [[t("inst.camp.apartament"), numeApartament(m, ap.id)] as [string, string]] : []),
      ...(cablu && s.circuit ? [[t("inst.camp.circuit"),
        `${s.circuit} · ${t(`inst.circuit.${tip as TipCircuit}` as const)}`] as [string, string]] : []),
      ...(cablu && s.rol !== "pat"
        ? [[t("inst.camp.cablu"), s.cablu ? numeCablu(s.cablu) : s.dn ? `${s.dn} mm²` : "—"] as [string, string],
           ...(s.tub_mm ? [[t("inst.camp.tub"), `Ø ${s.tub_mm} mm`] as [string, string]] : [])]
        : [[t("inst.camp.diametru"), s.sectiune ? `${cm(s.sectiune.l)} × ${cm(s.sectiune.h)} cm`
            : s.dn ? `DN ${s.dn} · Ø ${cm(s.d_m)} cm` : "—"] as [string, string]]),
      [t("inst.camp.material"), t(`inst.material.${s.material}` as const)],
      [t("inst.camp.mediu"), t(`inst.mediu.${s.mediu}` as const)],
      ...(s.panta ? [[t("inst.camp.panta"), `${numar(s.panta * 100, { maximumFractionDigits: 1 })} %`] as [string, string]] : []),
      [t("inst.camp.lungime"), `${numar(lungime(s.puncte), doua)} m`],
      [t("inst.camp.cote"), `${numar(z0, doua)} → ${numar(z1, doua)} m`],
      ...(cablu && s.terminale?.length
        ? [[t("inst.camp.de_la_la"), s.terminale.map(numeCapat).join(" → ")] as [string, string]] : []),
      ...(piese.length ? [[t("inst.camp.piese"), piese.join(", ")] as [string, string]] : []),
      ...(s.articol ? [[t("inst.camp.articol"), numeArticol(s)] as [string, string]] : []),
      ...(fitinguri.size
        ? [[t("inst.camp.fitinguri"), [...fitinguri].map(([k, n]) => `${n} × ${k}`).join(", ")] as [string, string]]
        : []),
    ];
  }

  return (
    <div className="card inst-ales">
      <table className="inst-tabel"><tbody>
        {randuri.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}
      </tbody></table>
      {s && s.puncte.length > 2 && (
        <details className="inst-tronsoane">
          <summary>{t("inst.camp.tronsoane", { n: s.puncte.length - 1 })}</summary>
          <table className="inst-tabel"><tbody>
            {s.puncte.slice(1).map((b, i) => {
              const a = s.puncte[i];
              const plan = Math.hypot(b[0] - a[0], b[1] - a[1]);
              const dz = b[2] - a[2];
              const L = Math.hypot(plan, dz);
              const fel = plan < 0.02 ? (dz < 0 ? "↓" : "↑")
                : s.panta ? `${numar((-dz / plan) * 100, { maximumFractionDigits: 1 })} %` : "→";
              return (
                <tr key={i}>
                  <td className="nr">{i + 1}</td>
                  <td className="nr">{numar(L, doua)} m</td>
                  <td className="nr">{fel}</td>
                </tr>
              );
            })}
          </tbody></table>
        </details>
      )}
      {ruta.length > 1 && (
        <div className="inst-ruta">
          <div className="inst-sursa">
            {t("inst.camp.spre")}: {ruta.slice(1, 4).map((x) => {
              const r = dupa.get(x);
              return r ? `${numeRol(t, r)}${r.dn && r.sistem !== "electrice" ? ` DN ${r.dn}` : ""}` : x;
            }).join(" → ")}{ruta.length > 4 ? " → …" : ""}
          </div>
          <button type="button" className="btn" onClick={() => inst.evidentiaza(rutaAprinsa ? [] : ruta)}>
            {rutaAprinsa ? t("inst.ruta.ascunde") : t("inst.ruta.arata", { n: ruta.length })}
          </button>
        </div>
      )}
      {sursa.length > 1 && (
        <div className="inst-ruta">
          <div className="inst-sursa">
            {t("inst.camp.de_la_la")}: {sursa.slice(1, 4).map((x) => {
              const r = dupa.get(x);
              if (!r) return x;
              const marime = r.sistem === "electrice" ? (r.cablu ? numeCablu(r.cablu) : r.dn ? `${r.dn} mm²` : "")
                : r.dn ? `DN ${r.dn}` : "";
              return `${numeRol(t, r)}${marime ? ` ${marime}` : ""}`;
            }).join(" ← ")}{sursa.length > 4 ? " ← …" : ""}
          </div>
          <button type="button" className="btn" onClick={() => inst.evidentiaza(sursaAprinsa ? [] : sursa)}>
            {sursaAprinsa ? t("inst.sursa.ascunde") : t("inst.sursa.arata", { n: sursa.length })}
          </button>
        </div>
      )}
      {circuit.length > 1 && (
        <div className="inst-ruta">
          <button type="button" className="btn" onClick={() => inst.evidentiaza(circuitAprins ? [] : circuit)}>
            {circuitAprins ? t("inst.circuit.ascunde") : t("inst.circuit.arata", { n: circuit.length })}
          </button>
        </div>
      )}
      {schema.length > 0 && <Schema circuite={schema} deschisa={tablouAles} />}
      {verdicte.length > 0 && (
        <div className="inst-verdicte">
          {verdicte.map((v) => (
            <span key={v.regula} title={v.sursa}>
              <span className={`inst-verdict ${v.verdict}`}>{v.verdict}</span> {v.regula}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Schema tabloului: un rând pe circuit — id, tip, camerele, punctele,
 *  puterea, cablul și tubul, siguranța și diferențialul, lungimea și
 *  căderea de tensiune; un clic aprinde firele circuitului în plan și 3D. */
function Schema({ circuite, deschisa }: { circuite: Circuit[]; deschisa: boolean }) {
  const t = useT();
  const inst = useInstalatii()!;
  const { numar } = useFormat();
  return (
    <details className="inst-tronsoane inst-schema" open={deschisa}>
      <summary>{t("inst.tablou.schema", { n: circuite.length })}</summary>
      <table className="inst-tabel"><tbody>
        {circuite.map((c) => {
          const aprins = c.segmente.length > 0 && c.segmente.every((x) => inst.evidentiate.has(x));
          const camere = c.dedicat ? din(t, `mobilier.${c.dedicat}`, c.dedicat)
            : c.camere.map((k) => din(t, `fel.camera.${k}`, k)).join(", ");
          return (
            <tr key={c.id} className={aprins ? "on" : ""} style={{ cursor: "pointer" }}
                onClick={() => inst.evidentiaza(aprins ? [] : c.segmente)}>
              <td><b>{c.id}</b><br /><span className="inst-sursa">{t(`inst.circuit.${c.tip}` as const)}</span></td>
              <td>{camere}<br /><span className="inst-sursa">
                {t("inst.tablou.puncte", { n: c.puncte })} · {numar(c.putere_w)} W</span></td>
              <td className="nr">{numeCablu(c.cablu)}<br /><span className="inst-sursa">Ø {c.tub_mm}</span></td>
              <td className="nr">{c.siguranta_a ? `${c.siguranta_a} A` : "—"}<br />
                <span className="inst-sursa">{c.rcd_ma ? `${c.rcd_ma} mA` : "—"}</span></td>
              <td className="nr">{numar(c.lungime_m, { maximumFractionDigits: 1 })} m<br />
                <span className="inst-sursa">ΔU {numar(c.cadere_pct, { maximumFractionDigits: 2 })} %</span></td>
            </tr>
          );
        })}
      </tbody></table>
    </details>
  );
}
